// Shareable links into the app: which tournament to open, WHICH OF ITS TABS to
// land on, or which registration to withdraw.
//
// Pure — no DOM, no state, no Firestore — so every shape below can be tested
// without a browser (see tests/deep-link.test.js). js/app.js calls parseDeepLink
// with `window.location` on startup and again on every hashchange; js/ui/
// navigation.js calls buildTournamentPath() on every tab change while watching.
//
// WHAT CHANGED, AND WHY
//
// A tab used to be addressed as a bare '#standings'. That is not a link to
// anything: it names a tab of whatever tournament the browser happened to have
// open, so pasting it to somebody else sent them to their own standings, or to
// an empty page. The canonical form below puts the tournament and the tab in
// one path, so every tab of every tournament has an address of its own that
// means the same thing in anybody's browser.

/** The kinds of deep link there are. Both carry exactly one id. */
export const DEEP_LINK_KINDS = Object.freeze(['tournament', 'unregister']);

/**
 * Every view id in index.html. Used to tell an app view hash ('#standings')
 * apart from anything else that may be sitting in the fragment.
 */
export const APP_VIEWS = Object.freeze([
    'landing', 'dashboard', 'tournaments', 'myplay', 'databases', 'organizers',
    'ongoing', 'upcoming', 'finished', 'settings', 'guide',
    'players', 'pairings', 'live', 'standings'
]);

/**
 * The tabs a watched tournament has, as URL segment -> view id.
 *
 * 'welcome' rather than 'dashboard' is deliberate: the view is called dashboard
 * in the markup for historical reasons, but what a spectator sees there is the
 * tournament's welcome page, and that is the word that belongs in a link
 * somebody reads before clicking it. The view id is still accepted as an alias
 * so an internally-built link cannot be wrong.
 */
export const TAB_TO_VIEW = Object.freeze({
    welcome: 'dashboard',
    dashboard: 'dashboard',
    players: 'players',
    pairings: 'pairings',
    standings: 'standings',
    live: 'live'
});

/** The view ids a watched tournament can show, in tab order. */
export const TOURNAMENT_VIEWS = Object.freeze(['dashboard', 'players', 'pairings', 'standings', 'live']);

const VIEW_TO_TAB = Object.freeze({
    dashboard: 'welcome',
    players: 'players',
    pairings: 'pairings',
    standings: 'standings',
    live: 'live'
});

function safeDecode(value) {
    try {
        return decodeURIComponent(value);
    } catch (e) {
        // A malformed escape ("%zz") throws. The raw value is still more useful
        // than nothing — an id that survived encoding unchanged reads correctly.
        return value;
    }
}

/** The view id a URL tab segment names, or '' when it names nothing we have. */
export function viewIdForTab(slug) {
    const key = String(slug || '').toLowerCase();
    return Object.prototype.hasOwnProperty.call(TAB_TO_VIEW, key) ? TAB_TO_VIEW[key] : '';
}

/** The URL segment for a view id, or '' when that view is not a tournament tab. */
export function tabForViewId(viewId) {
    const key = String(viewId || '');
    return Object.prototype.hasOwnProperty.call(VIEW_TO_TAB, key) ? VIEW_TO_TAB[key] : '';
}

/**
 * The canonical path for a tournament, optionally for one of its tabs.
 *
 *   buildTournamentPath('t1')               -> '/t/t1'
 *   buildTournamentPath('t1', 'standings')  -> '/t/t1/standings'
 *   buildTournamentPath('t1', 'dashboard')  -> '/t/t1'
 *
 * The welcome tab is left off: it is where '/t/<id>' already lands, and the
 * shortest form is the one people paste.
 */
export function buildTournamentPath(id, viewId) {
    const base = `/t/${encodeURIComponent(String(id || ''))}`;
    const tab = tabForViewId(viewId);
    return (!tab || tab === 'welcome') ? base : `${base}/${tab}`;
}

/** A full shareable URL for a tournament tab. `origin` is e.g. location.origin. */
export function buildTournamentLink(origin, id, viewId) {
    return `${String(origin || '').replace(/\/+$/, '')}${buildTournamentPath(id, viewId)}`;
}

/**
 * A full withdraw-my-registration URL.
 *
 * Still a QUERY STRING, and that is not an oversight: these go out by email, and
 * click-tracking redirectors routinely mangle or drop a '#'. The tournament form
 * above no longer needs the same care — a path has no fragment to lose — but an
 * unregister link carries a registration id that belongs to nobody's address bar,
 * so it stays a parameter rather than becoming a guessable path segment.
 */
//
// `token` is the registrant's cancel token (`&k=`), minted by the server when the
// registration is made: the id alone no longer cancels anything, and the link the
// server returns (cancelUrlFor in functions/lib/registration-api-handlers.js) is
// the one to show. Without a token this builds the older id-only form.
export function buildUnregisterLink(origin, id, token) {
    const base = `${String(origin || '').replace(/\/+$/, '')}/?unregister=${encodeURIComponent(String(id || ''))}`;
    return token ? `${base}&k=${encodeURIComponent(String(token))}` : base;
}

/**
 * Read a deep link out of a location-like object ({ hash, search, pathname }).
 *
 * FOUR SHAPES ARE ACCEPTED, AND EACH ONE IS LOAD-BEARING.
 *
 *   /t/<id> and /t/<id>/<tab>                                    CANONICAL
 *     What the app builds and what the address bar shows while a tournament is
 *     being watched. Served by the `/t/**` Hosting rewrite in firebase.json.
 *     It has no fragment and no query, so there is nothing in it for a mail
 *     redirector to damage — the failure that the two repair shapes below exist
 *     for cannot happen to this one.
 *
 *   ?tournament=<id>[&tab=<tab>] / ?t=<id>[&tab=<tab>] / ?unregister=<id>
 *     What links sent by EMAIL use, and what every Copy Link button produced
 *     before the canonical form existed. A query string survives being routed
 *     through a mail provider's click-tracking redirector, and it survives a
 *     host that cannot rewrite — which is why it stays supported rather than
 *     merely tolerated.
 *
 *   #tournament/<id>[/<tab>] / #unregister/<id>
 *     The original form, still sitting in confirmations already sent and in
 *     links people have saved. It works fine when typed or pasted directly.
 *
 *   /%23tournament/<id>
 *     The damaged form, and the reason the email had to change. Several mail
 *     clients percent-encode the '#' before redirecting, so the fragment arrives
 *     as part of the PATH and `location.hash` is empty — the app saw no link at
 *     all and silently did nothing, which is indistinguishable from a broken
 *     button. Recognising it turns a dead link back into a live one.
 *
 * Precedence is canonical path, then fragment, then query, then damaged path.
 * The canonical path leads because it is the only shape the app itself writes,
 * so when it is present it is what the browser is actually showing; the damaged
 * path trails because it is a repair rather than something anyone writes
 * deliberately.
 *
 * @param {{hash?: string, search?: string, pathname?: string}} loc
 * @returns {{tournament: string, unregister: string, tab: string, token: string}}
 *          at most one id is non-empty; `tab` is a VIEW ID (not a URL segment)
 *          and is only ever set alongside `tournament`; `token` is the cancel
 *          token (`&k=`) and only ever set alongside `unregister`.
 */
export function parseDeepLink(loc) {
    const out = { tournament: '', unregister: '', tab: '', token: '' };
    const source = loc || {};
    const rawHash = String(source.hash || '');
    const rawPath = String(source.pathname || '');

    // 1. The canonical path. Anchored at the root because that is where the
    //    rewrite puts it; a stray "/t/" deeper in some other path is not a link.
    //
    //    Split BEFORE decoding, and decode each segment on its own. The other
    //    way round, an id holding an encoded slash ("%2F") turns into a real one
    //    and the id splits in half across the id and tab segments — the id is
    //    then wrong and the tab is nonsense. (The damaged form below is the
    //    exact opposite case and decodes first on purpose: there, turning "%23"
    //    back into a '#' is the whole repair.)
    const canonical = rawPath.match(/^\/t\/([^/?#]+)(?:\/([^/?#]*))?\/?$/);
    if (canonical) {
        out.tournament = safeDecode(canonical[1]);
        out.tab = viewIdForTab(safeDecode(canonical[2] || ''));
        // '/t/<id>#standings' — no tab in the path, but one in the fragment.
        // Nobody's link builder writes this; a person typing one might.
        if (!out.tab) out.tab = viewIdForTab(rawHash.replace(/^#/, ''));
        return out;
    }

    // 2. The original fragment form. The id cannot contain a '/', so a second
    //    segment after it is unambiguously a tab.
    const hashMatch = rawHash.match(/^#(tournament|unregister)\/([^/]+)(?:\/([^/]*))?\/?$/);
    if (hashMatch) {
        out[hashMatch[1]] = safeDecode(hashMatch[2]);
        if (hashMatch[1] === 'tournament') out.tab = viewIdForTab(safeDecode(hashMatch[3] || ''));
        return out;
    }

    // 3. The query form. 't' is the short spelling; 'tournament' is what every
    //    link sent so far says.
    try {
        const params = new URLSearchParams(String(source.search || ''));
        const shortId = params.get('t');
        if (shortId) {
            out.tournament = shortId;
            out.tab = viewIdForTab(params.get('tab'));
            return out;
        }
        for (const kind of DEEP_LINK_KINDS) {
            const value = params.get(kind);
            if (value) {
                out[kind] = value;
                if (kind === 'tournament') out.tab = viewIdForTab(params.get('tab'));
                if (kind === 'unregister') out.token = params.get('k') || '';
                return out;
            }
        }
    } catch (e) { /* malformed query string — fall through to the path form */ }

    // 4. The damaged form. Decoded whole, because the '%23' that has to become a
    //    '#' again is the point of it. Anchored at the end so a site served from
    //    a subdirectory ("/app/#unregister/x" once encoded) still resolves.
    const pathMatch = safeDecode(rawPath).match(/[#/](tournament|unregister)\/([^/?#]+)\/?$/);
    if (pathMatch) out[pathMatch[1]] = safeDecode(pathMatch[2]);

    return out;
}

/** True when a location carries any deep link at all. */
export function hasDeepLink(loc) {
    const link = parseDeepLink(loc);
    return !!(link.tournament || link.unregister);
}

/**
 * The app view a bare '#<viewId>' fragment names, or '' for anything else.
 *
 * This is how the app's own tabs are addressed when NO tournament is being
 * watched — the arbiter's own pages, which belong to their account rather than
 * to anything shareable. Startup honours it over the remembered view, so
 * '/#databases' opens the databases page instead of wherever the last session
 * happened to end. A fragment that is part of a deep link is not a view hash
 * and is rejected here, so the two can never be read as each other.
 */
export function parseViewHash(loc) {
    const source = loc || {};
    if (hasDeepLink(source)) return '';
    const id = String(source.hash || '').replace(/^#/, '');
    return APP_VIEWS.includes(id) ? id : '';
}
